/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import { Box, Typography, Button, TextField, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr, enUS, zhCN, arSA } from 'date-fns/locale';
import styled from '@emotion/styled';
import type { SxProps, Theme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setStartDate, setEndDate, nextStep, setStepVerified } from '../rcSlice';

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const isDateBeforeToday = (isoStr: string | null) => {
  if (!isoStr) return false;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return true;
  return d < startOfToday();
};

const parseIsoToDate = (isoStr: string | null): Date | null => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Style "input arrondi" cohérent avec les autres champs RC/Habitation. On le
 * passe au DatePicker via slotProps.textField.sx (typage propre) plutôt que
 * via un slot styled-component (qui se mélange mal avec les types stricts du
 * x-date-pickers).
 */
const ROUND_INPUT_SX: SxProps<Theme> = {
  '& .MuiInputBase-root': { borderRadius: '30px' },
  '& input': { padding: '12px 16px' },
  '& .MuiInputLabel-root[data-shrink="false"]': {
    transform: 'translate(14px, 14px) scale(1)',
  },
};

// On garde un TextField stylé pour le champ "date de fin" (calculé, disabled)
// — celui-là est rendu en dehors du DatePicker donc on peut utiliser styled.
const TextfieldStyled = styled(TextField)`
  & .MuiInputBase-root {
    border-radius: 30px;
  }
  & input {
    padding: 12px 16px;
  }
  & .MuiInputLabel-root[data-shrink="false"] {
    transform: translate(14px, 14px) scale(1);
  }
`;

const DATE_LOCALES: Record<string, Locale> = {
  fr,
  en: enUS,
  zh: zhCN,
  ar: arSA,
};

const ContractDatesStep = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('rc');
  const { startDate, endDate } = useAppSelector(state => state.rc);

  const dateLocale =
    DATE_LOCALES[i18n.language?.split('-')[0] || 'fr'] || fr;

  // Si la date stockée est périmée (ex: wizard ouvert hier, repris aujourd'hui),
  // on la purge au mount.
  useEffect(() => {
    if (isDateBeforeToday(startDate)) {
      dispatch(setStartDate(null));
      dispatch(setEndDate(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startParsed = parseIsoToDate(startDate);
  const endParsed = parseIsoToDate(endDate);

  /**
   * Stocke la nouvelle date de début + calcule automatiquement la date de fin
   * à start + 1 an (la règle métier RC : contrats annuels). On ne rejette
   * jamais la saisie clavier intermédiaire — la validation finale est faite
   * dans `handleNext`.
   */
  const handleStartChange = (newDate: Date | null) => {
    if (!newDate || isNaN(newDate.getTime())) {
      dispatch(setStartDate(null));
      dispatch(setEndDate(null));
      return;
    }
    // Année à 1-2 chiffres pendant la saisie clavier : on ignore tant que ce
    // n'est pas un millésime réaliste, pour ne pas mettre le state dans un
    // état temporairement loufoque.
    if (newDate.getFullYear() < 1000) return;

    const now = new Date();
    const isToday =
      newDate.getFullYear() === now.getFullYear() &&
      newDate.getMonth() === now.getMonth() &&
      newDate.getDate() === now.getDate();
    const startISO = isToday
      ? now.toISOString()
      : new Date(
          Date.UTC(
            newDate.getFullYear(),
            newDate.getMonth(),
            newDate.getDate(),
            0,
            0,
            0,
            0,
          ),
        ).toISOString();

    // endDate = start + 1 an, toujours à minuit UTC.
    const endISO = new Date(
      Date.UTC(
        newDate.getFullYear() + 1,
        newDate.getMonth(),
        newDate.getDate(),
        0,
        0,
        0,
        0,
      ),
    ).toISOString();

    dispatch(setStartDate(startISO));
    dispatch(setEndDate(endISO));
  };

  const isValid = !!startDate && !!endDate;

  const handleNext = () => {
    if (!isValid) return;
    // Filet de sécurité : si la date a "vieilli" pendant que l'utilisateur
    // traînait sur le step (eg. journée passée), on bloque et on purge.
    if (isDateBeforeToday(startDate)) {
      toastr.warning(
        'Date périmée',
        'La date de début ne peut pas être antérieure à aujourd\'hui.',
      );
      dispatch(setStartDate(null));
      dispatch(setEndDate(null));
      return;
    }
    dispatch(setStepVerified(1));
    dispatch(nextStep());
  };

  // Formatte la date de fin (calculée auto) pour le champ disabled.
  const endDisplay = endParsed
    ? endParsed.toLocaleDateString(i18n.language || 'fr-FR')
    : '';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
      <Box sx={{ maxWidth: 500, mx: 'auto', p: 2 }}>
        <Typography variant="subtitle1" gutterBottom mb={0}>
          {t('step1.title')}
        </Typography>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12}>
            <DatePicker
              label={t('step1.start_date')}
              value={startParsed}
              onChange={handleStartChange}
              disablePast
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  sx: ROUND_INPUT_SX,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextfieldStyled
              label={t('step1.end_date')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDisplay}
              disabled
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'gray' }}>
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="body2">{t('step1.info')}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button onClick={handleNext} disabled={!isValid} variant="contained">
            {t('step1.next')}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ContractDatesStep;
